import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { finalize } from 'rxjs/operators';
import {
  AgreementOverviewDataExtractionQuestion,
  AgreementOverviewScreeningStage,
  AgreementQualityOverview,
} from '../../../dtos/agreement-quality-overview';
import {
  dataExtractionMetricLabel,
  dataExtractionQuestionTypeLabel,
  dataExtractionReasonLabel,
} from '../../../dtos/data-extraction-agreement';
import { ProjectState, formatStageState } from '../../../dtos/project-state';
import {
  AgreementInterpretation,
  AgreementThresholdStatus,
  agreementInterpretationLabel,
  agreementReasonLabel,
  agreementThresholdStatusLabel,
} from '../../../dtos/screening-agreement';
import { AgreementQualityOverviewService } from '../../../services/agreement-quality-overview.service';
import { BaseModalComponent } from '../../modals/base-modal/base-modal.component';

@Component({
  selector: 'app-completed-agreement-quality-overview',
  standalone: true,
  imports: [CommonModule, BaseModalComponent],
  templateUrl: './completed-agreement-quality-overview.component.html',
})
export class CompletedAgreementQualityOverviewComponent implements OnChanges {
  @Input() projectId = '';
  @Input() visible = false;

  readonly ProjectState = ProjectState;
  loading = false;
  errorMessage = '';
  overview: AgreementQualityOverview | null = null;
  detailsOpen = false;

  constructor(
    private overviewService: AgreementQualityOverviewService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] || changes['visible']) {
      this.loadIfReady();
    }
  }

  retry(): void {
    this.loadIfReady(true);
  }

  get screeningStagesMeasured(): string {
    if (!this.overview) {
      return '0 / 2';
    }
    const measured = [this.overview.titleAbstractScreening, this.overview.fullTextScreening].filter(
      (stage) => stage.availability === 'AVAILABLE',
    ).length;
    return `${measured} / 2`;
  }

  get metricsBelowThreshold(): number {
    if (!this.overview) {
      return 0;
    }
    const screeningBelow = [
      this.overview.titleAbstractScreening,
      this.overview.fullTextScreening,
    ].filter((stage) => stage.thresholdStatus === 'BELOW_THRESHOLD').length;
    const extractionBelow = this.overview.dataExtraction.questions.filter(
      (question) => question.thresholdStatus === 'BELOW_THRESHOLD',
    ).length;
    return screeningBelow + extractionBelow;
  }

  stageLabel(stage: ProjectState): string {
    return formatStageState(stage);
  }

  interpretationLabel(value: AgreementInterpretation | null | undefined = null): string {
    return agreementInterpretationLabel(value);
  }

  thresholdStatusLabel(value: AgreementThresholdStatus | null | undefined = null): string {
    return agreementThresholdStatusLabel(value);
  }

  screeningReasonLabel(stage: AgreementOverviewScreeningStage): string {
    return agreementReasonLabel(stage.nonComputableReason);
  }

  extractionReasonLabel(question: AgreementOverviewDataExtractionQuestion): string {
    return dataExtractionReasonLabel(question.nonComputableReason);
  }

  metricLabel(question: AgreementOverviewDataExtractionQuestion): string {
    return dataExtractionMetricLabel(question.metricType);
  }

  questionTypeLabel(question: AgreementOverviewDataExtractionQuestion): string {
    return dataExtractionQuestionTypeLabel(question.questionType);
  }

  questionLabel(question: AgreementOverviewDataExtractionQuestion): string {
    return question.questionText?.trim() || `Question ${question.questionId}`;
  }

  formatKappa(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return 'Not computable';
    }
    return value.toFixed(2);
  }

  formatPercent(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return 'Not available';
    }
    return `${value.toFixed(1)}%`;
  }

  formatNumber(value: number | null | undefined, digits = 1): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return 'Not available';
    }
    return value.toFixed(digits);
  }

  formatInteger(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return 'Not available';
    }
    return `${value}`;
  }

  finalizedAt(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  }

  screeningTitle(stage: AgreementOverviewScreeningStage): string {
    return stage.stage === ProjectState.TITLE_ABSTRACT_SCREENING
      ? 'Stage 2 - Title/Abstract Screening'
      : 'Stage 4 - Full-Text Screening';
  }

  extractionMetricResult(question: AgreementOverviewDataExtractionQuestion): string {
    if (question.metricType === 'NUMERIC_DIFFERENCE') {
      return `Mean ${this.formatNumber(question.meanAbsoluteDifference)}, median ${this.formatNumber(
        question.medianAbsoluteDifference,
      )}`;
    }
    if (question.metricType === 'UNWEIGHTED_COHEN_KAPPA') {
      return `Kappa ${this.formatKappa(question.cohenKappa)} | ${this.formatPercent(
        question.percentAgreement,
      )}`;
    }
    return this.formatPercent(question.percentAgreement);
  }

  trackQuestion(_: number, question: AgreementOverviewDataExtractionQuestion): number {
    return question.questionId;
  }

  private loadIfReady(force = false): void {
    if (!this.visible || !this.projectId) {
      this.overview = null;
      this.errorMessage = '';
      this.loading = false;
      this.markForCheck();
      return;
    }
    if (this.loading && !force) {
      return;
    }
    this.loading = true;
    this.errorMessage = '';
    this.overview = null;
    this.markForCheck();
    this.overviewService
      .getOverview(this.projectId)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.markForCheck();
        }),
      )
      .subscribe({
        next: (overview) => {
          this.overview = overview;
          this.markForCheck();
        },
        error: () => {
          this.errorMessage = 'Could not load agreement and quality statistics.';
          this.markForCheck();
        },
      });
  }

  private markForCheck(): void {
    this.changeDetectorRef.markForCheck();
  }
}
